/**
 * Spending: ammo needs, the in-game drone shop, and the pre-game loadout.
 */

import type { PlayerState, BotConfig, BotPersonality, WeaponKey, GameMode } from '../../types';
import type { BotShopResult, BotLoadoutResult } from './types';
import { WEAPONS, JUMPPACK_FUEL_COST } from '../../constants';
import type { SeededRandom } from '../../core/random';
import { DEFAULT_BOT_TUNING, tuningFor, type BotTuning } from './tuning';
import { hasUnlimitedJumpFuel } from '../../core/modeRules';

// =============================================================================
// AMMO NEEDS + IN-GAME SHOP
// =============================================================================

/**
 * Assess bot's ammo needs for intelligent shopping decisions.
 */
export function assessAmmoNeeds(
  player: PlayerState,
  personality: BotPersonality,
  rng?: SeededRandom,
  allowedWeapons?: WeaponKey[],
  tuning: BotTuning = DEFAULT_BOT_TUNING,
): { needsAmmo: boolean; urgency: number; priorities: WeaponKey[] } {
  const t = tuning.economy;
  const allSpecialKeys: WeaponKey[] = ['heavy', 'cluster', 'nuke', 'probe', 'punch'];
  const specialKeys = allowedWeapons
    ? allSpecialKeys.filter(k => allowedWeapons.includes(k))
    : allSpecialKeys;

  // Build priority order by personality
  let priorities: WeaponKey[];
  if (personality.aggression > t.aggressiveAbove) {
    priorities = ['heavy', 'cluster', 'punch', 'probe', 'nuke'];
  } else if (personality.aggression < t.defensiveBelow) {
    priorities = ['probe', 'punch', 'heavy', 'cluster', 'nuke'];
  } else if (personality.riskTolerance > t.chaoticMinRisk && rng) {
    // Chaotic: shuffle
    priorities = [...specialKeys];
    for (let i = priorities.length - 1; i > 0; i--) {
      const j = rng.int(0, i);
      [priorities[i], priorities[j]] = [priorities[j], priorities[i]];
    }
  } else {
    priorities = ['heavy', 'probe', 'punch', 'cluster', 'nuke'];
  }

  // Filter priorities by allowed weapons
  if (allowedWeapons) {
    priorities = priorities.filter(k => allowedWeapons.includes(k));
  }

  // Count ammo
  let totalSpecial = 0;
  let minAmmo = Infinity;
  let hasPriorityWeaponLow = false;

  for (const key of specialKeys) {
    const a = player.loadout.ammo[key];
    if (a === null || a === undefined) continue; // unlimited, skip
    totalSpecial += a;
    if (a < minAmmo) minAmmo = a;
  }

  // Check if priority weapons have enough ammo
  for (const key of priorities.slice(0, 3)) {
    const a = player.loadout.ammo[key];
    if (a === null || a === undefined) continue;
    if (a < t.lowPriorityAmmo) hasPriorityWeaponLow = true;
  }

  const needsAmmo = totalSpecial <= t.lowTotalSpecial || hasPriorityWeaponLow;

  // Urgency based on minimum ammo across special weapons
  let urgency: number;
  if (minAmmo === 0) urgency = t.urgencyEmpty;
  else if (minAmmo <= t.urgencyLowAt) urgency = t.urgencyLow;
  else if (minAmmo <= t.urgencyMidAt) urgency = t.urgencyMid;
  else urgency = 0;

  return { needsAmmo, urgency, priorities };
}

/** Matches where planetary transit is central (anything but plain deathmatch). */
function isTransitMode(mode: GameMode): boolean {
  return hasUnlimitedJumpFuel(mode);
}

/**
 * Decide what to buy in the in-game shop.
 */
export function computeBotShop(
  player: PlayerState,
  config: BotConfig,
  rng: SeededRandom,
  gameMode: GameMode = 'deathmatch',
  allowedWeapons?: WeaponKey[]
): BotShopResult | null {
  let budget = player.cbp;
  const { personality } = config;
  const tuning = tuningFor(config);
  const t = tuning.economy;
  const needs = assessAmmoNeeds(player, personality, rng, allowedWeapons, tuning);

  if (!needs.needsAmmo) return null;

  const purchases: Partial<Record<WeaponKey, number>> = {};

  // Buy weapons in priority order: 1 of each that the bot needs and can afford
  for (const key of needs.priorities) {
    if (key === 'standard') continue;
    const cost = WEAPONS[key].cbpCost;
    if (cost <= 0) continue;
    if (cost > budget) continue;

    const currentAmmo = player.loadout.ammo[key];
    if (currentAmmo === null) continue; // unlimited, don't buy
    if ((currentAmmo ?? 0) >= t.shopStockedAt) continue; // well-stocked for this weapon

    purchases[key] = 1;
    budget -= cost;

    if (budget < 1) break; // can't afford cheapest weapon (probe/punch = 1 CBP)
  }

  // Buy fuel if mobile and low on fuel
  // Transit modes: buy when fuel <= 1 (instead of only at 0), and all bots want fuel
  let fuelQty = 0;
  const fuelDeliveryCost = JUMPPACK_FUEL_COST * t.shopFuelDeliveryMultiplier; // delivery multiplier
  const transit = isTransitMode(gameMode);
  const fuelThreshold = transit ? 1 : 0;
  const wantsShopFuel = transit || personality.mobility > t.mobileFuelAbove;
  if (player.jumppackFuel <= fuelThreshold && wantsShopFuel && budget >= fuelDeliveryCost) {
    fuelQty = 1;
    budget -= fuelDeliveryCost;
  }

  const total = Object.values(purchases).reduce((s, v) => s + (v ?? 0), 0);
  if (total === 0 && fuelQty === 0) return null;

  return { purchases, fuelQty };
}

// =============================================================================
// PRE-GAME SHOP
// =============================================================================

/**
 * Auto-purchase a loadout for a bot in the pre-game shop.
 * Returns ammo counts to purchase (not including standard which is unlimited).
 */
export function computeBotLoadout(
  budget: number,
  personality: BotPersonality,
  rng: SeededRandom,
  gameMode: GameMode = 'deathmatch',
  allowedWeapons?: WeaponKey[],
  tuning: BotTuning = DEFAULT_BOT_TUNING,
): BotLoadoutResult {
  const t = tuning.economy;
  const purchases: Partial<Record<WeaponKey, number>> = {
    heavy: 0,
    cluster: 0,
    nuke: 0,
    probe: 0,
    punch: 0,
    antigrav: 0,
    gravBomb: 0,
    tether: 0,
    terraformer: 0,
    emp: 0,
    drill: 0,
    fire: 0,
    mortar: 0,
  };

  // Reserve fuel budget for mobile bots (transit modes reserve 3x for planetary transit)
  const transit = isTransitMode(gameMode);
  const wantsFuel = transit || personality.mobility > t.mobileFuelAbove;
  const baseFuelCount = transit ? t.loadoutFuelTransit : (personality.mobility > t.loadoutFuelMobileAbove ? t.loadoutFuelMobile : t.loadoutFuelDefault);
  const fuelReserve = wantsFuel ? baseFuelCount * JUMPPACK_FUEL_COST : 0;
  let remaining = budget - fuelReserve;

  // Define shopping preferences based on personality
  type ShopItem = { key: WeaponKey; priority: number };
  const items: ShopItem[] = [];

  if (personality.aggression > t.aggressiveAbove) {
    // Aggressive: heavy, nuke, cluster, drill, fire
    items.push({ key: 'heavy', priority: 5 });
    items.push({ key: 'nuke', priority: 3 });
    items.push({ key: 'cluster', priority: 2 });
    items.push({ key: 'punch', priority: 1 });
    items.push({ key: 'probe', priority: 1 });
    items.push({ key: 'drill', priority: 3 });
    items.push({ key: 'fire', priority: 2 });
    items.push({ key: 'emp', priority: 2 });
    items.push({ key: 'gravBomb', priority: 1 });
  } else if (personality.aggression < t.defensiveBelow) {
    // Defensive: punch, probe, heavy, terraformer, tether
    items.push({ key: 'punch', priority: 4 });
    items.push({ key: 'probe', priority: 4 });
    items.push({ key: 'heavy', priority: 2 });
    items.push({ key: 'cluster', priority: 1 });
    items.push({ key: 'terraformer', priority: 2 });
    items.push({ key: 'tether', priority: 2 });
    items.push({ key: 'antigrav', priority: 1 });
  } else if (personality.riskTolerance > t.chaoticMinRisk) {
    // Chaotic: random spread of everything
    items.push({ key: 'heavy', priority: 2 + rng.int(0, 3) });
    items.push({ key: 'cluster', priority: 2 + rng.int(0, 3) });
    items.push({ key: 'nuke', priority: 2 + rng.int(0, 3) });
    items.push({ key: 'probe', priority: 2 + rng.int(0, 3) });
    items.push({ key: 'punch', priority: 2 + rng.int(0, 3) });
    items.push({ key: 'antigrav', priority: 1 + rng.int(0, 2) });
    items.push({ key: 'gravBomb', priority: 1 + rng.int(0, 2) });
    items.push({ key: 'fire', priority: 1 + rng.int(0, 2) });
    items.push({ key: 'emp', priority: 1 + rng.int(0, 2) });
  } else {
    // Balanced
    items.push({ key: 'heavy', priority: 3 });
    items.push({ key: 'cluster', priority: 2 });
    items.push({ key: 'probe', priority: 2 });
    items.push({ key: 'punch', priority: 2 });
    items.push({ key: 'nuke', priority: 1 });
    items.push({ key: 'fire', priority: 1 });
    items.push({ key: 'emp', priority: 1 });
    items.push({ key: 'drill', priority: 1 });
  }

  // §12.7 S4: the field class is bought only where the match names it (allowedWeapons) — default matches unchanged
  if (allowedWeapons?.includes('mortar')) items.push({ key: 'mortar', priority: 3 });

  // Sort by priority (descending) and filter by allowed weapons
  items.sort((a, b) => b.priority - a.priority);
  const filteredItems = allowedWeapons
    ? items.filter(i => allowedWeapons.includes(i.key))
    : items;

  // Buy items in priority order, cycling through until budget exhausted
  let maxPasses = 20;
  while (remaining > 0 && maxPasses > 0) {
    let boughtAny = false;
    for (const item of filteredItems) {
      const cost = WEAPONS[item.key].cbpCost;
      if (cost <= 0) continue;
      if (cost <= remaining) {
        purchases[item.key] = (purchases[item.key] ?? 0) + 1;
        remaining -= cost;
        boughtAny = true;

        // Don't buy too many of any one thing
        const count = purchases[item.key] ?? 0;
        if (count >= item.priority) {
          item.priority = 0; // stop buying
        }
      }
    }
    if (!boughtAny) break;
    maxPasses--;
  }

  // Mobile bots buy fuel from reserved budget + any leftover
  // Transit modes buy 3x for planetary transit
  remaining += fuelReserve;
  let fuelPurchased = 0;
  if (wantsFuel && remaining >= JUMPPACK_FUEL_COST) {
    const fuelCount = baseFuelCount;
    for (let i = 0; i < fuelCount; i++) {
      if (remaining >= JUMPPACK_FUEL_COST) {
        fuelPurchased++;
        remaining -= JUMPPACK_FUEL_COST;
      }
    }
  }

  return { purchases, fuelPurchased };
}
